import React from 'react';
import { CountingCirclesCandidate } from 'api/api.types';
import { FormattedCandidate } from 'lib/CandidatePortraits/CandidatePortraits';
import { useTranslation } from 'react-i18next';

export type CountingCirclesCandidateWithLocation = CountingCirclesCandidate & {
  countingCircle: string;
  bfsNumber: string;
};

export type PortraitPosition = {
  bottom: number;
  left?: number;
  right?: number;
};

interface Props {
  portraitPosition: PortraitPosition;
  showTooltip: boolean;
  hoveredCandidate: FormattedCandidate;
}

const TOOLTIP_MAXWIDTH_MOBILE = 160;
const TOOLTIP_MAXWIDTH_DESKTOP = 280;

const PortraitCloudTooltip = ({
  portraitPosition,
  showTooltip,
  hoveredCandidate,
}: Props) => {
  const { t } = useTranslation('wap');

  if (!hoveredCandidate) return null;

  const isMediumWindow = window.innerWidth < 768;
  const TOOLTIP_MAXWIDTH = isMediumWindow
    ? TOOLTIP_MAXWIDTH_MOBILE
    : TOOLTIP_MAXWIDTH_DESKTOP;

  const tooltipTooFarRight =
    window.innerWidth - portraitPosition.right < TOOLTIP_MAXWIDTH * 1.1;

  const tooltipTooFarLeft = portraitPosition.left < TOOLTIP_MAXWIDTH * 1.1;

  const getTransformStyle = () => {
    // on smaller screen, we generally show the tooltip on the left hand side of the portrait
    // if there is not enough space left of portrait, we show it aligned with left side of screen plus 3rem safe zone
    if (isMediumWindow) {
      if (tooltipTooFarLeft) {
        return `translate(2rem, calc(${portraitPosition.bottom}px + 3rem))`;
      }
      return `translate(calc(${portraitPosition.left}px - 100% - 2rem), calc(${portraitPosition.bottom}px + 2rem))`;
    }

    // on larger screens, we show the tooltip on the right hand side of the portrait
    // if there is not enough space right of portrait, we show it left of portrait
    if (tooltipTooFarRight) {
      return `translate(calc(${portraitPosition.left}px - 100% - 2rem), calc(${portraitPosition.bottom}px + 2rem))`;
    }

    return `translate(calc(${portraitPosition.right}px + 2rem), calc(${portraitPosition.bottom}px + 2rem))`;
  };

  const transformStyle = getTransformStyle();

  const getName = () => {
    if (hoveredCandidate.firstName || hoveredCandidate.lastName) {
      return (
        <>
          {hoveredCandidate?.firstName} <strong>{hoveredCandidate?.lastName}</strong>
        </>
      );
    }
    return <strong>{t('open_seat')}</strong>;
  };

  const getInOfficeSinceString = () => {
    if (hoveredCandidate.isUpToNow === null) {
      return '';
    }
    // non-breaking spaces to prevent line break between list name and "new"/"in office since"
    if (!hoveredCandidate.isUpToNow) {
      return `\u00A0${t('new')}`;
    }
    return `\u00A0${t('is_up_to_now')} ${
      hoveredCandidate.inOfficeSince
        ? `(${t('since', {
            year: hoveredCandidate.inOfficeSince,
          })})`
        : ''
    }`;
  };

  return (
    <div
      className="portraitcloud__tooltip"
      style={{
        transform: transformStyle,
        opacity: showTooltip ? 1 : 0,
      }}
    >
      <div>{getName()}</div>
      <div className="portraitcloud__tooltip-meta">
        <span>{hoveredCandidate?.listName}</span>
        <span className="portraitcloud__tooltip-inofficesince">
          {getInOfficeSinceString()}
        </span>
      </div>
    </div>
  );
};

export default PortraitCloudTooltip;
